import DescriptionIcon from '@mui/icons-material/Description';
import SettingsIcon from '@mui/icons-material/Settings';
import {
    Box,
    Button,
    Divider,
    IconButton,
    Paper,
    Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useCalculator } from '../../contexts/CalculatorContext';
import { usePricing } from '../../contexts/PricingContext';
import { useTabs } from '../../contexts/TabsContext';
import { useUserPreferences } from '../../contexts/UserPreferencesContext';
import { usePricingSnapshot } from '../../hooks/usePricingSnapshot';
import { createComparison, createOffer } from '../../services/api';
import {
  convertUSDToSubscriptionAmount,
  formatDisplayPrice,
  formatUSDInternalDisplayPrice,
} from '../../utils/currencyUtils';
import { calculateSubscriptionPlans } from '../../utils/subscriptionCalculator';
import { MonobankIcon, ABankIcon } from '../icons/BankIcons';
import CurrencySelector from '../shared/CurrencySelector';
import OfferGenerationDialog from './modals/OfferGenerationDialog';
import OfferSuccessDialog from './modals/OfferSuccessDialog';

/**
 * PriceSummary component displays the pricing summary and total
 * @param {Object} props - Component props
 * @param {Array} props.summaryItems - Array of items to display in the summary
 * @param {number} props.total - Total price
 * @param {string} props.currency - Currency symbol
 * @param {React.RefObject} props.summaryRef - Ref for the summary element
 * @param {Object} props.summaryStyle - Style object for the summary
 * @param {Object} props.subscriptionPrices - Subscription prices
 * @param {boolean} props.show90DaySubscription - Flag to show 90-day option
 * @param {boolean} props.show180DaySubscription - Flag to show 180-day option
 * @param {boolean} props.show12MonthContract - Flag to show 12-month contract option
 * @param {boolean} props.showSplitPayment - Flag to show split payment option
 * @param {string} props.region - Current region (for conditional display)
 */
const PriceSummary = ({ 
  summaryItems, 
  summaryItemsForSubscription, // Base prices for subscription calculator
  total, 
  currency, 
  summaryRef,
  summaryStyle = {},
  subscriptionPrices = {},
  show90DaySubscription,
  show180DaySubscription,
  show12MonthContract,
  showSplitPayment,
  onSubscriptionSettingsClick
}) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { region, displayCurrency } = useUserPreferences();
  const { isAuthenticated, user } = useAuth();
  const { tabs, activeTab, updateActiveTabData } = useTabs();
  const { generateSnapshotForActiveTab, generateSnapshotForInactiveTab } = usePricingSnapshot();
  
  // Get currency rates for display conversion
  // Always call hooks unconditionally, then handle undefined values
  const { currencyRates = {} } = usePricing() || {};
  const { phoneNumbers, enablePhoneActivation, eniqAiImplementationFee, enableEniqImplementationFee, voicebotImplementationFee, enableVoicebotImplementationFee, isProductSelected, showPricesWithoutBonuses } = useCalculator();
  
  // State for dialogs
  const [offerDialogOpen, setOfferDialogOpen] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [generatedOfferUrl, setGeneratedOfferUrl] = useState('');
  const [offerType, setOfferType] = useState('offer'); // 'offer' or 'comparison'
  const [loading, setLoading] = useState(false);

  // Get subscription settings from active tab
  const subscriptionSettings = activeTab?.settings?.subscriptionSettings || {};
  const subscription90Days = subscriptionSettings.subscription90Days || false;
  const subscription180Days = subscriptionSettings.subscription180Days || false;
  const subscription12Month = subscriptionSettings.subscription12Month || false;
  const subscriptionSplitPayment = subscriptionSettings.subscriptionSplitPayment || false;

  // Get custom discounts from activeTab settings (top level, not in subscriptionSettings)
  const tabCustomDiscount360 = activeTab?.settings?.customDiscount360 ?? 20;
  const tabCustomDiscount180 = activeTab?.settings?.customDiscount180 ?? 0;

  // Calculate one-time payments (activation fees + implementation fees)
  const oneTimePayments = React.useMemo(() => {
    const payments = [];

    // 1. Phone numbers activation fees
    if (enablePhoneActivation && isProductSelected && isProductSelected('phone-numbers') && phoneNumbers && phoneNumbers.length > 0) {
      const totalActivationFee = phoneNumbers.reduce((sum, phone) => {
        return sum + (phone.quantity * (phone.activationFee || 0));
      }, 0);

      if (totalActivationFee > 0) {
        payments.push({
          description: t('calculator.phoneNumberActivation', 'Phone numbers activation'),
          total: totalActivationFee
        });
      }
    }

    // 2. Eniq AI implementation fee
    if (enableEniqImplementationFee && isProductSelected && isProductSelected('eniq-ai') && eniqAiImplementationFee > 0) {
      payments.push({
        description: t('calculator.eniqImplementationFee', 'Eniq AI Implementation'),
        total: eniqAiImplementationFee
      });
    }

    // 3. VoiceBot implementation fee
    if (enableVoicebotImplementationFee && isProductSelected && isProductSelected('voicebot') && voicebotImplementationFee > 0) {
      payments.push({
        description: t('calculator.voicebotImplementationFeeLabel', 'VoiceBot Implementation'),
        total: voicebotImplementationFee
      });
    }

    return payments;
  }, [enablePhoneActivation, enableEniqImplementationFee, enableVoicebotImplementationFee, isProductSelected, phoneNumbers, eniqAiImplementationFee, voicebotImplementationFee, t]);

  // Handle offer generation
  const handleGenerateOffer = () => {
    setOfferDialogOpen(true);
  };

  const handleOfferDialogClose = () => {
    setOfferDialogOpen(false);
  };

  const handleOfferGenerate = async (selectedTabs, type, clientName) => {
    if (!isAuthenticated) {
      console.error('❌ User not authenticated!');
      return;
    }
    
    try {
      setLoading(true);
      setOfferDialogOpen(false);
      
      if (type === 'offer' && selectedTabs.length === 1) {
        // Create single offer
        const tabData = selectedTabs[0];
        
        // Generate pricing snapshot using new hook
        const pricing_snapshot = generateSnapshotForActiveTab(tabData);
        
        const offerData = {
          tab_data: tabData,
          region: region,
          currency: currency,
          language: i18n.language,
          total_price: total,
          pricing_snapshot,
          client_name: clientName,
          // For backward compatibility
          calculated_pricing: {
            summaryItems: summaryItemsForSubscription || summaryItems
          }
        };
        
        const response = await createOffer(offerData);
        const offerUrl = `${window.location.origin}/offers/${response.data.id}`;
        
        setGeneratedOfferUrl(offerUrl);
        setOfferType('offer');
        setSuccessDialogOpen(true);
      } else if (type === 'comparison' && selectedTabs.length > 1) {
        // Create comparison - calculate pricing for each tab
        const tabsWithPricing = [];
        
        for (const tab of selectedTabs) {
          try {
            // Determine if this is the active tab
            const isActiveTab = tab.id === activeTab?.id;
            
            // Generate pricing snapshot using new hook
            const pricing_snapshot = isActiveTab ? 
              generateSnapshotForActiveTab(tab) : 
              await generateSnapshotForInactiveTab(tab);
            
            tabsWithPricing.push({
              ...tab,
              calculatedPricing: {
                summaryItems: pricing_snapshot.individual_prices,
                subscriptionPlans: pricing_snapshot.subscription_plans,
                totalMonthly: pricing_snapshot.total_monthly
              },
              pricing_snapshot
            });
          } catch (error) {
            console.error('❌ [PriceSummary] Error calculating pricing for tab:', tab.name, error);
            // Continue with original tab data without calculated pricing
            tabsWithPricing.push(tab);
          }
        }
        
        const comparisonData = {
          tabs: tabsWithPricing,
          region: region,
          currency: currency,
          language: i18n.language,
          client_name: clientName
        };
        
        const response = await createComparison(comparisonData);
        const comparisonUrl = `${window.location.origin}/comparisons/${response.data.id}`;
        
        setGeneratedOfferUrl(comparisonUrl);
        setOfferType('comparison');
        setSuccessDialogOpen(true);
      }
    } catch (error) {
      console.error('Error generating offer:', error);
      // TODO: Show error message
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessDialogClose = () => {
    setSuccessDialogOpen(false);
    setGeneratedOfferUrl('');
  };


  // Calculate subscription plans using the actual bundle-discounted total
  const subscriptionOptions = {
    show360Days: true, // Always shown
    show180Days: show180DaySubscription,
    show90Days: show90DaySubscription,
    show12Month: show12MonthContract && total >= 250, // Only show if enabled AND monthly cost >= 250
    showSplitPayment: showSplitPayment && region === 'ua' // Only show for Ukraine region
  };

  // Use base prices for subscription calculation, but pass bundle total as baseline for 360-day plan
  const subscriptionData = calculateSubscriptionPlans(
    summaryItemsForSubscription || [], 
    subscriptionOptions, 
    region,
    total, // Pass bundle-discounted total as baseline for default plan
    summaryItems, // Pass bundle-discounted products for proper subscription discount calculation
    tabCustomDiscount360, // Pass custom 360-day discount percentage
    tabCustomDiscount180  // Pass custom 180-day discount percentage
  );
  
  // Add one-time payments to subscription payment amounts
  const totalOneTimePayments = oneTimePayments.reduce(
    (sum, payment) => sum + convertUSDToSubscriptionAmount(payment.total, region, currencyRates),
    0
  );
  const { plans: originalPlans, showSavings, defaultPlan } = subscriptionData;
  const plans = originalPlans.map(plan => ({
    ...plan,
    paymentAmount: plan.paymentAmount + totalOneTimePayments
  }));

  return (
    <Box ref={summaryRef} sx={{ ...summaryStyle }}>
      <Paper 
        elevation={0} 
        sx={{ 
          bgcolor: 'white', 
          borderRadius: theme.spacing(1), 
          border: '1px solid rgba(0, 0, 0, 0.12)', 
          p: 3,
          position: 'sticky',
          top: theme.spacing(3)
        }}
      >
        {/* Currency Selector - only for Ukraine */}
        <CurrencySelector sx={{ mb: 2 }} />
        
        <Typography variant="subtitle1" sx={{ mb: 2.5, fontWeight: 600, fontSize: 18 }}>
          {t('calculator.monthlyForecast', 'Monthly fee forecast')}
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Typography variant="h3" sx={{ color: '#00C853', fontWeight: 700, fontSize: 36 }}>
            {formatDisplayPrice(showPricesWithoutBonuses ? total : (defaultPlan?.monthlyPrice || total), displayCurrency, currencyRates, region)}
          </Typography>
          <Typography sx={{ fontSize: 16, color: '#00C853', alignSelf: 'flex-end', mb: 0.8 }}>
            {t('calculator.perMonthShort', '/mo')}
          </Typography>
        </Box>
        
        <Divider sx={{ mb: 2 }} />
        
        {/* Summary Items - show bundle-discounted prices with subscription discount applied */}
        {summaryItems && summaryItems.map((item, index) => {
          // Apply subscription discount to bundle-discounted prices for display
          const productId = item.productType || 
                          (item.label?.includes('Business Phone') ? 'business-phone' :
                          item.label?.includes('FMC') ? 'fmc' :
                          item.label?.includes('Call Tracking') ? 'call-tracking' :
                          item.label?.includes('Callback') ? 'callback' :
                          item.label?.includes('Chat') ? 'omnichannel-chat' : 'other');
          
          const isEligibleForSubscriptionDiscount = ['business-phone', 'fmc', 'call-tracking', 'callback', 'omnichannel-chat', 'ai-conversation'].includes(productId);
          // Apply custom 360-day subscription discount to eligible products
          const discountFactor = 1 + (tabCustomDiscount360 / 100); // Convert custom percentage to factor
          const displayPrice = (showPricesWithoutBonuses || !isEligibleForSubscriptionDiscount) ? item.price : item.price / discountFactor;
          
          return (
            <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Box>
                <Typography color="text.primary" fontSize={14} fontWeight={500}>
                  {item.label || item.title}
                </Typography>
                {/* Show metadata for additional product info (e.g., Voice Traffic with overage) */}
                {item.metadata && (
                  <Typography color="text.secondary" fontSize={12} sx={{ mt: 0.2 }}>
                    {item.metadata}
                  </Typography>
                )}
              </Box>
              <Typography fontWeight={600} fontSize={14} sx={{ textAlign: 'right' }}>
                {formatDisplayPrice(displayPrice, displayCurrency, currencyRates, region)}
              </Typography>
            </Box>
          );
        })}
        
        <Divider sx={{ my: 3 }} />

        {/* One-time payments section */}
        {oneTimePayments.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
              {t('calculator.oneTimePayments', 'One-time payments')}
            </Typography>

            {oneTimePayments.map((payment, index) => (
              <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography color="text.primary" fontSize={14} fontWeight={500}>
                  {payment.description}
                </Typography>
                <Typography fontWeight={600} fontSize={14}>
                  {formatUSDInternalDisplayPrice(payment.total, region, displayCurrency, currencyRates)}
                </Typography>
              </Box>
            ))}

            <Divider sx={{ my: 3 }} />
          </Box>
        )}

        {/* Subscription Plans */}
        {plans.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {t('calculator.subscriptionOptions', 'Subscription Options')}
              </Typography>
              <IconButton
                onClick={onSubscriptionSettingsClick}
                size="small"
                sx={{ 
                  color: (subscription90Days || subscription180Days || subscription12Month || subscriptionSplitPayment) ? '#00C853' : 'text.secondary',
                  '&:hover': {
                    color: 'primary.main',
                    backgroundColor: 'action.hover'
                  },
                  ml: 1
                }}
              >
                <SettingsIcon fontSize="inherit" />
              </IconButton>
            </Box>
            
            {plans.map((plan) => (
              <Paper 
                key={plan.id}
                elevation={0} 
                sx={{ 
                  p: 2, 
                  mb: 1.5, 
                  bgcolor: '#fff',
                  border: plan.isRecommended ? '2px solid' : '1px solid',
                  borderColor: plan.isRecommended ? 'rgba(0, 200, 83, 0.35)' : 'rgba(0, 0, 0, 0.10)',
                  borderRadius: 1
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {(() => {
                      // Use same logic as SubscriptionPlanDisplay with custom discount percentages
                      if (plan.id === '360-days') {
                        // Hide bonus text if discount is 0%
                        if (tabCustomDiscount360 === 0) {
                          return t('subscription.360DaysNoBonus', '360 days');
                        }
                        return showSavings
                          ? t('subscription.360DaysCustom', `360 days (bonus {{discount}}%)`, { discount: tabCustomDiscount360 })
                          : t('subscription.360DaysNoBonus', '360 days');
                      }
                      if (plan.id === '180-days') {
                        // Hide bonus text if discount is 0%
                        if (tabCustomDiscount180 === 0) {
                          return t('subscription.180Days', '180 days');
                        }
                        return t('subscription.180DaysCustom', `180 days (bonus {{discount}}%)`, { discount: tabCustomDiscount180 });
                      }

                      // Static translations for other plans
                      const labels = {
                        '90-days': t('subscription.90Days', '90 days'),
                        '12-month-contract': t('subscription.12MonthContract', '12-monthly contract'),
                        '360-days-split': t('subscription.360DaysSplit', '360 days (installment)')
                      };
                      return labels[plan.id] || plan.id;
                    })()}
                  </Typography>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
{formatDisplayPrice(plan.paymentAmount, displayCurrency, currencyRates, region)} {t('subscription.toPay', 'to pay')}
                    </Typography>
                    {region === 'ua' && (
                      <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25, fontVariantNumeric: 'tabular-nums' }}>
                        {t('subscription.vatIncluded', 'З ПДВ 20%: {{amount}}', { amount: formatDisplayPrice(plan.paymentAmount * 1.2, displayCurrency, currencyRates, region) })}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mt: 1.5 }}>
                  <Box>
                    <Typography variant="body2" sx={{ color: '#00C853', fontWeight: 600 }}>
{formatDisplayPrice(plan.monthlyPrice, displayCurrency, currencyRates, region)}{t('subscription.perMonth', '/mo')}
                    </Typography>
                    {plan.savings > 0 && showSavings && (
                      <Typography variant="caption" sx={{ color: '#00C853', display: 'block', mt: 0.25 }}>
 {t('subscription.savings', 'Savings')} {formatDisplayPrice(plan.savings, displayCurrency, currencyRates, region)}
                      </Typography>
                    )}
                  </Box>
                  {plan.id === '360-days-split' && (
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                      <MonobankIcon size={20} />
                      <ABankIcon size={20} />
                    </Box>
                  )}
                </Box>
              </Paper>
            ))}
            
            <Divider sx={{ my: 3 }} />
          </Box>
        )}
        
        {/* Generate Offer Button */}
        <Button 
          variant="contained" 
          color="primary" 
          fullWidth 
          onClick={handleGenerateOffer}
          disabled={loading}
          startIcon={<DescriptionIcon />}
          sx={{ 
            borderRadius: 1, 
            py: 1.5, 
            fontWeight: 600, 
            boxShadow: '0 3px 10px rgba(0,200,83,0.3)',
            backgroundColor: '#00C853',
            '&:hover': {
              backgroundColor: '#00A040',
              boxShadow: '0 4px 12px rgba(0,200,83,0.4)'
            }
          }}
        >
          {t('calculator.generateOffer', 'Згенерувати офер')}
        </Button>
      </Paper>

      {/* Offer Generation Dialog */}
      <OfferGenerationDialog
        open={offerDialogOpen}
        onClose={handleOfferDialogClose}
        onGenerate={handleOfferGenerate}
        tabs={tabs}
      />
      
      {/* Success Dialog */}
      <OfferSuccessDialog
        open={successDialogOpen}
        onClose={handleSuccessDialogClose}
        offerUrl={generatedOfferUrl}
        offerType={offerType}
      />

    </Box>
  );
};

export default PriceSummary;
